import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FaGithub, FaBlog, FaEnvelope, FaTwitter, FaDiscord } from "react-icons/fa";
import { motion } from "framer-motion";

const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitted(false);

    // Simulate form submission delay
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000); // Auto-hide after 3s
    }, 2000);
  };

  return (
    <motion.footer
      className="footer-glass py-8 sm:py-12 px-4 sm:px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      role="contentinfo"
    >
      <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
        {/* About */}
        <div className="text-center">
          <h3 className="text-lg sm:text-xl font-semibold accent-text mb-4">{t("footer.about")}</h3>
          <p className="text-sm sm:text-base">{t("footer.about_desc")}</p>
        </div>
        {/* Quick Links */}
        <div className="text-center">
          <h3 className="text-lg sm:text-xl font-semibold accent-text mb-4">{t("footer.quick_links")}</h3>
          <ul className="space-y-2">
            <li>
              <a href={`/${lang || "en"}/services`} className="accent-text hover:text-gray-700 transition-all text-sm sm:text-base">
                {t("navbar.services")}
              </a>
            </li>
            <li>
              <a href={`/${lang || "en"}/donate`} className="accent-text hover:text-gray-700 transition-all text-sm sm:text-base">
                {t("navbar.donate")}
              </a>
            </li>
            <li>
              <a href="https://blog.iburtum.xyz" className="accent-text hover:text-gray-700 transition-all text-sm sm:text-base">
                {t("footer.blog")}
              </a>
            </li>
          </ul>
        </div>
        {/* Social Media */}
        <div className="text-center">
          <h3 className="text-lg sm:text-xl font-semibold accent-text mb-4">{t("footer.social_media")}</h3>
          <div className="flex justify-center space-x-4 sm:space-x-6">
            <a href="https://github.com/madebybrt" className="accent-text hover:text-gray-700 transition-all">
              <FaGithub className="text-xl sm:text-2xl" />
            </a>
            <a href="https://twitter.com/madebybrt" className="accent-text hover:text-gray-700 transition-all">
              <FaTwitter className="text-xl sm:text-2xl" />
            </a>
            <a href="https://discord.iburtum.xyz" className="accent-text hover:text-gray-700 transition-all">
              <FaDiscord className="text-xl sm:text-2xl" />
            </a>
            <a href="mailto:info@iburtum.xyz" className="accent-text hover:text-gray-700 transition-all">
              <FaEnvelope className="text-xl sm:text-2xl" />
            </a>
          </div>
        </div>
        {/* Newsletter Signup */}
        <div className="text-center">
          <h3 className="text-lg sm:text-xl font-semibold accent-text mb-4">{t("footer.newsletter")}</h3>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col items-center">
            <div className="custom-input w-full max-w-xs">
              <input
                type="email"
                name="email"
                id="newsletter-email"
                placeholder=" "
                className="w-full"
                required
              />
              <label htmlFor="newsletter-email">{t("footer.newsletter_placeholder")}</label>
            </div>
            <button
              type="submit"
              className="px-4 py-2 mt-2 bg-transparent border border-gray-500 accent-text hover:bg-gray-500 hover:text-white rounded transition-all text-sm sm:text-base flex items-center justify-center"
              disabled={loading}
            >
              {loading ? <div className="loading-spinner w-4 h-4 mr-2"></div> : t("footer.subscribe")}
            </button>

            {/* ✅ Green Check */}
            {submitted && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0 }}
                className="mt-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white"
              >
                ✓
              </motion.div>
            )}
          </form>
        </div>
      </div>
      {/* Terms and Privacy Links */}
      <div className="container mx-auto mt-6 sm:mt-8 flex justify-center space-x-4">
        <a
          href="https://brt.iburtum.xyz/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-transparent border border-gray-500 accent-text hover:bg-gray-500 hover:text-white rounded transition-all text-sm sm:text-base"
        >
          {t("footer.terms")}
        </a>
        <a
          href="https://brt.iburtum.xyz/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-transparent border border-gray-500 accent-text hover:bg-gray-500 hover:text-white rounded transition-all text-sm sm:text-base"
        >
          {t("footer.privacy")}
        </a>
      </div>
      <p className="text-center mt-6 sm:mt-8 text-sm sm:text-base">{t("footer.copyright")}</p>
    </motion.footer>
  );
};

export default Footer;
